import React, { useState, useEffect, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import toast from 'react-hot-toast'
import {Lock, User, Eye, EyeOff, Loader2, TreePine, FileText, Users, BarChart3, CheckCircle2, ArrowRight, Leaf, Shield, Download, Globe, X, UserCircle} from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'
import { loginComBanco, bootstrapAdminMaster } from '@/utils/bootstrapAdmin'
import SolicitacaoPublicaPage from '@/pages/SolicitacaoPublicaPage'

// ── Paleta ──────────────────────────────────────────────────────────────────
const GREEN_DARK  = '#2d6124'
const GREEN_MID   = '#3a7d2c'
const TEAL        = '#0f3b50'

// ── Logo GEOA ────────────────────────────────────────────────────────────────
function GeoaIcon({ size = 56 }: { size?: number }) {
  return (
    <img
      src="https://static.lumi.new/24/2440432f58c0c7516f7c4599415c87d6.webp"
      alt="GEOA"
      width={size}
      height={size}
      style={{ objectFit: 'contain' }}
    />
  )
}

// ── Folha decorativa SVG ─────────────────────────────────────────────────────
function LeafDecor({ className = '' }: { className?: string }) {
  return (
    <svg viewBox="0 0 60 80" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
      <path d="M30 75 C10 55 0 35 10 15 C20 -5 50 5 50 30 C50 55 30 75 30 75Z"
        fill="currentColor" opacity="0.18" />
      <path d="M30 75 L30 30" stroke="currentColor" strokeWidth="1.5" opacity="0.25" strokeLinecap="round" />
    </svg>
  )
}

// ── Card de funcionalidade ───────────────────────────────────────────────────
function FeatureCard({
  icon: Icon, title, desc
}: { icon: React.ElementType; title: string; desc: string }) {
  return (
    <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-5 flex gap-4 items-start">
      <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
        style={{ backgroundColor: 'rgba(255,255,255,0.18)' }}>
        <Icon className="w-5 h-5 text-white" />
      </div>
      <div>
        <p className="text-white font-semibold text-sm leading-tight">{title}</p>
        <p className="text-green-100/80 text-xs mt-0.5 leading-relaxed">{desc}</p>
      </div>
    </div>
  )
}



// ── Componente principal ─────────────────────────────────────────────────────
export default function LoginPage() {
  const navigate  = useNavigate()
  const location  = useLocation()
  const { login, isAuthenticated } = useAuth()

  const [userName,   setUserName]   = useState('')
  const [senha,      setSenha]      = useState('')
  const [showSenha,  setShowSenha]  = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [showPortal, setShowPortal] = useState(false)

  const inputRef = useRef<HTMLInputElement>(null)
  const from = (location.state as { from?: string } | null)?.from || '/dashboard'

  useEffect(() => {
    if (isAuthenticated) navigate(from, { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const u = userName.trim()
    const p = senha
    if (!u || !p) { toast.error('Preencha utilizador e senha.'); return }

    setSubmitting(true)
    try {
      await bootstrapAdminMaster()
      const sessionUser = await loginComBanco(u, p)
      if (!sessionUser) { toast.error('Utilizador ou senha incorretos.'); return }
      login(sessionUser)
      toast.success(`Bem-vindo, ${sessionUser.userName}!`)
      navigate(from, { replace: true })
    } catch (err) {
      console.error('[GEOA] login → erro inesperado:', err)
      toast.error('Erro ao entrar. Tente novamente.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">

      {/* ── Painel esquerdo – apresentação ──────────────────────────────────── */}
      <div
        className="relative flex flex-col justify-between overflow-hidden lg:w-[58%] px-8 py-10 lg:px-16 lg:py-14"
        style={{
          background: `linear-gradient(145deg, ${GREEN_DARK} 0%, ${GREEN_MID} 45%, ${TEAL} 100%)`
        }}
      >
        {/* Decorações de fundo */}
        <LeafDecor className="absolute -top-8 -right-4 w-40 h-52 text-green-300 rotate-12 pointer-events-none" />
        <LeafDecor className="absolute bottom-0 -left-6 w-32 h-44 text-green-300 -rotate-20 pointer-events-none" />
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgba(255,255,255,0.04) 0%, transparent 70%)' }}
        />

        {/* Logo topo */}
        <div className="relative z-10">
          <div className="flex items-center gap-3">
            <img
              src="https://static.lumi.new/d5/d5273e538c910aa2d1297ad10a07dcb7.webp"
              alt="GEOA – Gestão Online Ambiental"
              className="h-10 lg:h-12 object-contain"
            />
          </div>
        </div>

        {/* Conteúdo central */}
        <div className="relative z-10 my-10 lg:my-0">
          <div className="inline-flex items-center gap-2 bg-white/15 border border-white/25 rounded-full px-4 py-1.5 mb-6">
            <Leaf className="w-3.5 h-3.5 text-green-200" />
            <span className="text-green-100 text-xs font-medium tracking-wide">
              Prefeitura Municipal de São José do Seridó/RN
            </span>
          </div>

          <h1 className="text-3xl lg:text-4xl xl:text-5xl font-extrabold text-white leading-tight tracking-tight max-w-xl">
            Gestão Ambiental<br />
            <span style={{ color: '#a8e063' }}>inteligente</span> e<br />
            eficiente
          </h1>

          <p className="text-green-100/80 text-sm lg:text-base mt-4 max-w-md leading-relaxed">
            Plataforma integrada para gestão de solicitações de remoção de árvores,
            controlo ambiental e emissão de documentos oficiais do município.
          </p>

          {/* Botões de ação — Solicitar Online + Portal do Cidadão */}
          <div className="mt-8 flex flex-col gap-3">
            {/* Solicitar Remoção Online */}
            <button
              onClick={() => setShowPortal(true)}
              className="relative flex items-center gap-4 w-full px-6 py-5 rounded-2xl transition-all group shadow-xl active:scale-[0.98] overflow-hidden"
              style={{ backgroundColor: '#ffffff' }}
              onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#f0fdf4' }}
              onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#ffffff' }}
            >
              <span className="absolute inset-0 rounded-2xl ring-2 ring-white/60 animate-pulse pointer-events-none" />
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm"
                style={{ backgroundColor: GREEN_MID }}
              >
                <Globe className="w-6 h-6 text-white" />
              </div>
              <div className="text-left flex-1">
                <p className="font-extrabold text-base leading-tight" style={{ color: GREEN_DARK }}>Solicitar Remoção Online</p>
                <p className="text-xs mt-0.5 font-medium" style={{ color: '#4b7a3c' }}>Abra sua solicitação — sem precisar de login</p>
              </div>
              <div
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl flex-shrink-0 transition-all group-hover:gap-2.5"
                style={{ backgroundColor: GREEN_MID }}
              >
                <span className="text-white text-xs font-bold">Acessar</span>
                <ArrowRight className="w-4 h-4 text-white transition-transform group-hover:translate-x-0.5" />
              </div>
            </button>

            {/* Portal do Cidadão — destaque reforçado */}
            <button
              onClick={() => navigate('/portal-cidadao')}
              className="relative flex items-center gap-4 w-full px-6 py-5 rounded-2xl transition-all group shadow-xl active:scale-[0.98] overflow-hidden border-2"
              style={{ backgroundColor: '#ffffff', borderColor: GREEN_MID }}
              onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#f0fdf4' }}
              onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#ffffff' }}
            >
              {/* Pulsação sutil igual ao botão de solicitação */}
              <span className="absolute inset-0 rounded-2xl ring-2 ring-green-400/40 animate-pulse pointer-events-none" />
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm"
                style={{ backgroundColor: TEAL }}
              >
                <UserCircle className="w-6 h-6 text-white" />
              </div>
              <div className="text-left flex-1">
                <p className="font-extrabold text-base leading-tight" style={{ color: TEAL }}>Portal do Cidadão</p>
                <p className="text-xs mt-0.5 font-medium" style={{ color: '#1a5470' }}>Acompanhe seus processos com CPF e senha</p>
              </div>
              <div
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl flex-shrink-0 transition-all group-hover:gap-2.5"
                style={{ backgroundColor: TEAL }}
              >
                <span className="text-white text-xs font-bold">Entrar</span>
                <ArrowRight className="w-4 h-4 text-white transition-transform group-hover:translate-x-0.5" />
              </div>
            </button>
          </div>

          {/* Banner Lei 426 */}
          <a
            href="https://www.saojosedoserido.rn.gov.br/arquivos/113/Leis%20Municipais_426_2018_0000001.pdf"
            target="_blank"
            rel="noopener noreferrer"
            download="Lei_426_2018.pdf"
            className="mt-6 flex items-center gap-4 w-full rounded-2xl border border-white/25 bg-white/10 hover:bg-white/20 backdrop-blur-sm px-5 py-4 transition-all group"
          >
            <div className="w-11 h-11 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
              <FileText className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white font-bold text-sm leading-tight">Lei Municipal nº 426/2018</p>
              <p className="text-green-100/70 text-xs mt-0.5 leading-snug">
                Legislação que regulamenta a remoção e poda de árvores no município
              </p>
            </div>
            <div className="flex items-center gap-1.5 bg-white/25 group-hover:bg-white/40 rounded-xl px-3 py-1.5 flex-shrink-0 transition-all">
              <Download className="w-3.5 h-3.5 text-white" />
              <span className="text-white text-xs font-semibold">Baixar PDF</span>
            </div>
          </a>

          {/* Features */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-6">
            <FeatureCard
              icon={FileText}
              title="Solicitações Online"
              desc="Abertura e acompanhamento de processos de remoção de árvores"
            />
            <FeatureCard
              icon={BarChart3}
              title="Relatórios e Dashboard"
              desc="Análises visuais e relatórios institucionais em PDF"
            />
            <FeatureCard
              icon={TreePine}
              title="Gestão Ambiental"
              desc="Controlo completo do arvoredo urbano do município"
            />
            <FeatureCard
              icon={Users}
              title="Catadores Cadastrados"
              desc="Registo e carteirinha dos catadores municipais"
            />
          </div>
        </div>

        {/* Rodapé esquerdo */}
        <div className="relative z-10">
          <p className="text-green-100/50 text-xs">
            © {new Date().getFullYear()} GEOA – Gestão Online Ambiental. Todos os direitos reservados.
          </p>
        </div>
      </div>

      {/* ── Painel direito – login ───────────────────────────────────────────── */}
      <div className="flex flex-col items-center justify-center bg-gray-50 lg:w-[42%] px-6 py-12 lg:px-12">
        <div className="w-full max-w-sm">

          {/* Ícone GEOA + título */}
          <div className="flex flex-col items-center mb-8">
            <div
              className="w-20 h-20 rounded-3xl flex items-center justify-center mb-4 shadow-lg"
              style={{ backgroundColor: '#f0f7ed' }}
            >
              <GeoaIcon size={52} />
            </div>
            <h2 className="text-xl font-bold text-gray-800 text-center">Acesso ao Sistema</h2>
            <p className="text-sm text-gray-500 mt-1 text-center">
              Entre com as suas credenciais para continuar
            </p>
          </div>

          {/* Card do formulário */}
          <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-7">

            {/* Badge de segurança */}
            <div className="flex items-center gap-2 bg-green-50 border border-green-100 rounded-2xl px-3 py-2 mb-6">
              <Shield className="w-4 h-4 flex-shrink-0" style={{ color: GREEN_MID }} />
              <p className="text-xs text-green-700 font-medium">
                Acesso restrito a utilizadores autorizados
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">

              {/* Campo Utilizador */}
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                  Utilizador
                </label>
                <div className="relative">
                  <User className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    ref={inputRef}
                    type="text"
                    value={userName}
                    onChange={(e) => setUserName(e.target.value)}
                    placeholder="Digite o seu utilizador"
                    autoComplete="username"
                    disabled={submitting}
                    className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-2xl text-sm outline-none transition bg-gray-50/50 focus:bg-white"
                    onFocus={e => {
                      e.currentTarget.style.borderColor = GREEN_MID
                      e.currentTarget.style.boxShadow  = `0 0 0 3px ${GREEN_MID}18`
                    }}
                    onBlur={e => {
                      e.currentTarget.style.borderColor = '#e5e7eb'
                      e.currentTarget.style.boxShadow  = ''
                    }}
                  />
                </div>
              </div>

              {/* Campo Senha */}
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                  Senha
                </label>
                <div className="relative">
                  <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type={showSenha ? 'text' : 'password'}
                    value={senha}
                    onChange={(e) => setSenha(e.target.value)}
                    placeholder="Digite a sua senha"
                    autoComplete="current-password"
                    disabled={submitting}
                    className="w-full pl-10 pr-11 py-3 border border-gray-200 rounded-2xl text-sm outline-none transition bg-gray-50/50 focus:bg-white"
                    onFocus={e => {
                      e.currentTarget.style.borderColor = GREEN_MID
                      e.currentTarget.style.boxShadow  = `0 0 0 3px ${GREEN_MID}18`
                    }}
                    onBlur={e => {
                      e.currentTarget.style.borderColor = '#e5e7eb'
                      e.currentTarget.style.boxShadow  = ''
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowSenha(v => !v)}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition"
                    tabIndex={-1}
                  >
                    {showSenha ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {/* Botão entrar */}
              <button
                type="submit"
                disabled={submitting}
                className="w-full text-white font-bold py-3 rounded-2xl transition-all flex items-center justify-center gap-2 mt-2 shadow-sm disabled:opacity-60 disabled:cursor-not-allowed active:scale-[0.98]"
                style={{ backgroundColor: GREEN_MID }}
                onMouseEnter={e => { if (!submitting) (e.currentTarget as HTMLButtonElement).style.backgroundColor = GREEN_DARK }}
                onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = GREEN_MID }}
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    A entrar...
                  </>
                ) : (
                  <>
                    Entrar no Sistema
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </form>
          </div>

          {/* Selos de confiança */}
          <div className="flex items-center justify-center gap-5 mt-6">
            {[
              { icon: CheckCircle2, label: 'Seguro' },
              { icon: Shield,       label: 'Oficial' },
              { icon: Leaf,         label: 'Ambiental' },
            ].map(({ icon: Icon, label }) => (
              <div key={label} className="flex items-center gap-1.5">
                <Icon className="w-3.5 h-3.5" style={{ color: GREEN_MID }} />
                <span className="text-xs text-gray-500 font-medium">{label}</span>
              </div>
            ))}
          </div>

          {/* Rodapé mobile */}
          <p className="text-center text-xs text-gray-400 mt-6 lg:hidden">
            © {new Date().getFullYear()} Prefeitura Municipal de São José do Seridó/RN
          </p>
        </div>
      </div>

      {/* ── Modal Portal Público ─────────────────────────────────────────────── */}
      {showPortal && (
        <div className="fixed inset-0 bg-black/60 z-50 overflow-y-auto">
          <div className="min-h-screen flex items-start justify-center py-6 px-4">
            <div className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl relative">
              <button
                onClick={() => setShowPortal(false)}
                className="absolute top-4 right-4 z-10 w-8 h-8 rounded-xl bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-500 hover:text-gray-700 transition"
              >
                <X className="w-4 h-4" />
              </button>
              <div className="p-6">
                <SolicitacaoPublicaPage onClose={() => setShowPortal(false)} />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
